import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { api } from '@/lib/api/client'

export type LmsOutcome =
  | 'RECONCILED'
  | 'ALREADY_RECONCILED'
  | 'DUPLICATE_IN_FILE'
  | 'NOT_FOUND'
  | 'OUT_OF_SCOPE'
  | 'AMOUNT_MISMATCH'
  | 'NOT_CONFIRMED'
  | 'INVALID'

export const OUTCOME_LABELS: Record<LmsOutcome, string> = {
  RECONCILED: 'Reconciled',
  ALREADY_RECONCILED: 'Already reconciled',
  DUPLICATE_IN_FILE: 'Duplicate in file',
  NOT_FOUND: 'Not found',
  OUT_OF_SCOPE: 'Outside your scope',
  AMOUNT_MISMATCH: 'Amount differs',
  NOT_CONFIRMED: 'Not confirmed',
  INVALID: 'Incomplete row',
}

export interface LmsBatch {
  batchId: string
  batchNo: number
  fileName: string
  uploadedByEmpId: string
  uploadedByName: string
  uploadedAt: string
  rowsInFile: number
  reconciledCount: number
  reconciledAmount: number
  alreadyCount: number
  rejectedCount: number
  status: 'ACTIVE' | 'REVERTED'
  revertedAt: string | null
  revertedByEmpId: string | null
  revertedByName: string | null
  revertReason: string | null
}

export interface LmsBatchDetail {
  batch: LmsBatch
  /** File rows per outcome. */
  outcomes: Partial<Record<LmsOutcome, number>>
}

export interface LmsBatchRow {
  rowNumber: number
  receiptNo: string | null
  loanAccountNo: string | null
  amount: number | null
  collectionId: string | null
  outcome: LmsOutcome
  detail: string | null
}

export interface LmsCollection {
  collectionId: string
  receiptNo: string
  loanAccountNo: string
  customerName: string | null
  amount: number
  collectedAt: string | null
  channel: string
  mode: string
  status: string
  empId: string | null
  agentName: string | null
  branchCode: string | null
  /** Exceptions only: the batch that reconciled it. */
  batchId: string | null
  batchNo: number | null
}

export interface Page<T> {
  items: T[]
  total: number
}

export interface Unreconciled extends Page<LmsCollection> {
  totalAmount: number
}

export interface UnreconciledFilters {
  from?: string | undefined
  to?: string | undefined
}

const KEY = ['lms-recon'] as const

export function useUnreconciled(filters: UnreconciledFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...KEY, 'unreconciled', filters, page, pageSize],
    queryFn: async () =>
      (await api.get<Unreconciled>('/v1/lms-reconciliation/unreconciled', {
        params: { ...filters, limit: pageSize, offset: page * pageSize },
      })).data,
    placeholderData: keepPreviousData,
  })
}

export function useLmsExceptions() {
  return useQuery({
    queryKey: [...KEY, 'exceptions'],
    queryFn: async () => (await api.get<LmsCollection[]>('/v1/lms-reconciliation/exceptions')).data,
  })
}

export function useLmsBatches(page: number, pageSize: number) {
  return useQuery({
    queryKey: [...KEY, 'batches', page, pageSize],
    queryFn: async () =>
      (await api.get<Page<LmsBatch>>('/v1/lms-reconciliation/batches', {
        params: { limit: pageSize, offset: page * pageSize },
      })).data,
    placeholderData: keepPreviousData,
  })
}

/** Only fetched once a batch is opened. */
export function useLmsBatch(batchId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'batch', batchId],
    queryFn: async () => (await api.get<LmsBatchDetail>(`/v1/lms-reconciliation/batches/${batchId}`)).data,
    enabled: batchId !== null,
  })
}

export function useLmsBatchRows(batchId: string | null, outcome: LmsOutcome | null, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...KEY, 'batch-rows', batchId, outcome, page, pageSize],
    queryFn: async () =>
      (await api.get<Page<LmsBatchRow>>(`/v1/lms-reconciliation/batches/${batchId}/rows`, {
        params: { outcome: outcome ?? undefined, limit: pageSize, offset: page * pageSize },
      })).data,
    enabled: batchId !== null,
    placeholderData: keepPreviousData,
  })
}

export function useUploadLmsBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return (await api.post<LmsBatchDetail>('/v1/lms-reconciliation/batches', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useRevertLmsBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { batchId: string; reason: string }) =>
      (await api.post<LmsBatchDetail>(`/v1/lms-reconciliation/batches/${vars.batchId}/revert`, {
        reason: vars.reason,
      })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

const ERRORS: Record<string, string> = {
  not_an_xlsx: "That file isn't an Excel workbook. Upload the .xlsx repayments export.",
  not_the_repayments_export:
    "That isn't the repayments export — it needs the LOAN NO, Receipt No and Amount columns. Download it from Collections and upload it unchanged.",
  empty_file: 'The file has no rows.',
  too_many_rows: 'The file has more than 50,000 rows. Split it and upload each part.',
  file_required: 'Choose the repayments file first.',
  reason_required: 'Say why the batch is being reverted.',
  reason_too_long: 'Keep the reason under 500 characters.',
  already_reverted: 'This batch was already reverted.',
  batch_not_found: "This batch doesn't exist or isn't yours to see.",
}

/** The API's `code: message` detail in words; [fallback] when there is none. */
export function lmsReconErrorMessage(err: unknown, fallback: string): string {
  if (!isAxiosError(err)) return fallback
  if (err.response?.status === 403) return "You don't have permission to reconcile collections."
  const data = err.response?.data as { detail?: string; message?: string } | undefined
  const detail = data?.detail ?? data?.message ?? ''
  const code = detail.split(':')[0]?.trim() ?? ''
  return ERRORS[code] ?? fallback
}
